import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Zap, Wand2, ImagePlus, Sparkles, ArrowLeftRight } from "lucide-react";

// Import generated images
import beforePortrait from "@/assets/showcase/before-portrait.jpg";
import afterPortrait from "@/assets/showcase/after-portrait.jpg";
import beforeBeauty from "@/assets/showcase/before-beauty.jpg";
import afterBeauty from "@/assets/showcase/after-beauty.jpg";
import beforeLandscape from "@/assets/showcase/before-landscape.jpg";
import afterLandscape from "@/assets/showcase/after-landscape.jpg";

const showcaseItems = [
  {
    id: "generation",
    label: "Génération",
    icon: Sparkles,
    before: beforePortrait,
    after: afterPortrait,
    prompt: "Portrait professionnel avec éclairage studio",
    time: "3.2s",
    gradient: "from-rose-500 to-pink-500"
  },
  {
    id: "retouche",
    label: "Retouche",
    icon: Wand2,
    before: beforeBeauty,
    after: afterBeauty,
    prompt: "Amélioration beauté naturelle + correction couleurs",
    time: "2.1s",
    gradient: "from-blue-500 to-cyan-500"
  },
  {
    id: "transformation",
    label: "Transformation",
    icon: ImagePlus,
    before: beforeLandscape,
    after: afterLandscape,
    prompt: "Transformation en style artistique abstrait",
    time: "4.5s",
    gradient: "from-violet-500 to-purple-500"
  },
];

export const ShowcaseSection = () => {
  const [activeTab, setActiveTab] = useState("generation");
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const activeItem = showcaseItems.find(item => item.id === activeTab);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent | MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    setSliderPosition((x / rect.width) * 100);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove as any);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove as any);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <section ref={sectionRef} className="py-28 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary/5 blur-3xl rounded-full" />
      <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-accent/5 blur-3xl rounded-full" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span 
            className={`inline-block text-sm font-medium text-primary mb-4 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Démonstration
          </span>
          <h2 
            className={`text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 font-display transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Voyez la{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">magie</span>{" "}
            en action
          </h2>
          <p 
            className={`text-lg text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Glissez pour comparer. Des résultats professionnels en quelques secondes.
          </p>
        </div>

        {/* Tabs with glassmorphism */}
        <div 
          className={`flex justify-center gap-2 mb-12 transition-all duration-700 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="inline-flex p-1.5 bg-card/80 backdrop-blur-xl rounded-full border border-border/50">
            {showcaseItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={cn(
                  "flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300",
                  activeTab === item.id
                    ? "bg-gradient-floop text-white shadow-lg shadow-primary/30"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Before/After Slider */}
        {activeItem && (
          <div 
            className={`max-w-4xl mx-auto transition-all duration-700 delay-400 ${
              isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-8 scale-95"
            }`}
          >
            {/* Glow effect */}
            <div className={`absolute inset-0 bg-gradient-to-r ${activeItem.gradient} rounded-3xl blur-3xl opacity-10`} />
            
            {/* Slider container */}
            <div 
              ref={sliderRef}
              className="relative aspect-[3/2] rounded-3xl overflow-hidden border border-border/50 shadow-2xl cursor-ew-resize select-none"
              onMouseDown={() => setIsDragging(true)}
              onMouseMove={handleMouseMove}
            >
              {/* After image (bottom layer) */}
              <div className="absolute inset-0">
                <img
                  src={activeItem.after}
                  alt="After"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                {/* After label */}
                <div className="absolute top-4 right-4 bg-gradient-floop px-4 py-2 rounded-full text-sm font-semibold text-white flex items-center gap-2 shadow-lg">
                  <Sparkles className="w-4 h-4" />
                  Après
                </div>
              </div>
              
              {/* Before image (top layer, clipped) */}
              <div 
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
              >
                <img
                  src={activeItem.before}
                  alt="Before"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                {/* Before label */}
                <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-foreground shadow-lg">
                  Avant
                </div>
              </div>
              
              {/* Slider handle */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-white shadow-xl"
                style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-2xl flex items-center justify-center">
                  <ArrowLeftRight className="w-6 h-6 text-primary" />
                </div>
              </div>
            </div>

            {/* Info bar */}
            <div className="mt-6 bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${activeItem.gradient} flex items-center justify-center shadow-lg`}>
                  <activeItem.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Prompt utilisé</p>
                  <p className="text-foreground font-medium">"{activeItem.prompt}"</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-success/10 text-success px-5 py-2.5 rounded-full border border-success/20">
                <Zap className="w-5 h-5" />
                <span className="font-bold">Généré en {activeItem.time}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
